import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Shield, AlertTriangle, Lock, TrendingUp } from 'lucide-react'
import { useOrg } from '../context/OrgContext'
import { getSummaryStats, getRecentBreaches } from '../api/endpoints'
import LoadingSpinner from '../components/LoadingSpinner'
import RiskScoreBadge from '../components/RiskScoreBadge'
import { format } from 'date-fns'

interface SummaryCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  color: string
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, icon, color }) => {
  return (
    <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-text-muted text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-text-primary mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      </div>
    </div>
  )
}

const Dashboard: React.FC = () => {
  const { currentOrg } = useOrg()

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['summary-stats', currentOrg?.id],
    queryFn: () => getSummaryStats(currentOrg!.id),
    enabled: !!currentOrg,
  })

  const { data: recentBreaches, isLoading: breachesLoading } = useQuery({
    queryKey: ['recent-breaches', currentOrg?.id],
    queryFn: () => getRecentBreaches(currentOrg!.id, 10),
    enabled: !!currentOrg,
  })

  if (!currentOrg || statsLoading) {
    return <LoadingSpinner fullPage message="Loading dashboard..." />
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Dashboard</h1>
        <p className="text-text-muted mt-1">Overview of your security posture</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          title="Monitored Accounts"
          value={stats?.total_monitored || 0}
          icon={<Shield className="w-6 h-6" />}
          color="bg-accent-primary/10 text-accent-primary"
        />
        <SummaryCard
          title="Active Breaches (30d)"
          value={stats?.active_breaches || 0}
          icon={<AlertTriangle className="w-6 h-6" />}
          color="bg-risk-high/10 text-risk-high"
        />
        <SummaryCard
          title="Locked Accounts"
          value={stats?.locked_accounts || 0}
          icon={<Lock className="w-6 h-6" />}
          color="bg-risk-critical/10 text-risk-critical"
        />
        <SummaryCard
          title="Avg Risk Score"
          value={stats?.average_risk_score || 0}
          icon={<TrendingUp className="w-6 h-6" />}
          color="bg-risk-medium/10 text-risk-medium"
        />
      </div>

      {/* Recent Breach Events */}
      <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Recent Breach Events</h2>

        {breachesLoading ? (
          <LoadingSpinner size="md" message="Loading recent breaches..." />
        ) : recentBreaches && recentBreaches.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Source</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Risk Score</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Breach Name</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentBreaches.map((breach) => (
                  <tr key={breach.id} className="border-b border-zinc-700/50">
                    <td className="py-3 px-4 text-text-primary text-sm">
                      {format(new Date(breach.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        breach.source === 'hibp' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'
                      }`}>
                        {breach.source.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <RiskScoreBadge score={breach.risk_score} showLabel={false} />
                    </td>
                    <td className="py-3 px-4 text-text-primary text-sm">
                      {breach.breach_name || 'Unknown'}
                    </td>
                    <td className="py-3 px-4 text-text-muted text-sm">
                      {breach.action_taken || 'None'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-text-muted text-center py-8">No recent breach events</p>
        )}
      </div>
    </div>
  )
}

export default Dashboard
